import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { lightTheme, darkTheme } from "../styles/theme";
import { HiMoon, HiSun, HiMenu, HiX } from "react-icons/hi";

const Nav = styled.nav<{ theme: "light" | "dark"; scrolled: boolean }>`
  padding: 10px 5%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  width: 100%;
  background-color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  z-index: 100;
  border-bottom: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ scrolled, theme }) =>
    scrolled
      ? theme === "light"
        ? "0 5px 0 " + lightTheme.accent
        : "0 5px 0 " + darkTheme.accent
      : "none"};
  transition: box-shadow 0.3s ease;

  @media (min-width: 768px) {
    padding: 12px 10%;
  }
`;

const Logo = styled(motion.a)<{ theme: "light" | "dark" }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.surface};
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  transition: all 0.2s ease;
  border-radius: 50%;
  width: 42px;
  height: 42px;

  &:hover {
    transform: translate(-2px, -2px) scale(1.05);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  }

  &:active {
    transform: translate(0, 0);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? "2px 2px 0 " + lightTheme.border
        : "2px 2px 0 " + darkTheme.border};
  }

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const DesktopNavLinks = styled.div`
  display: none;
  gap: 15px;
  align-items: center;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavLink = styled(motion.a)<{ theme: "light" | "dark"; active?: boolean }>`
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  font-size: 0.75rem;
  font-weight: 800;
  cursor: pointer;
  padding: 6px 12px;
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  background: ${({ active, theme }) =>
    active
      ? theme === "light"
        ? lightTheme.accent
        : darkTheme.accent
      : theme === "light"
        ? lightTheme.surface
        : darkTheme.surface};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    transform: translate(-2px, -2px);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadow : darkTheme.shadow};
    background: ${({ theme }) =>
      theme === "light" ? lightTheme.accent : darkTheme.accent};
  }

  &:active {
    transform: translate(0, 0);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? "2px 2px 0 " + lightTheme.border
        : "2px 2px 0 " + darkTheme.border};
  }
`;

const ThemeToggle = styled(motion.button)<{ theme: "light" | "dark" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  font-size: 1rem;
  cursor: pointer;
  padding: 8px;
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  transition: all 0.2s ease;
  width: 38px;
  height: 38px;

  &:hover {
    transform: translate(-2px, -2px) rotate(15deg);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  }

  &:active {
    transform: translate(0, 0) rotate(0deg);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? "2px 2px 0 " + lightTheme.border
        : "2px 2px 0 " + darkTheme.border};
  }
`;

const MobileMenuButton = styled(motion.button)<{ theme: "light" | "dark" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  font-size: 1.3rem;
  cursor: pointer;
  padding: 8px;
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.surface : darkTheme.surface};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  transition: all 0.2s ease;
  width: 38px;
  height: 38px;

  @media (min-width: 768px) {
    display: none;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MobileNav = styled(motion.div)<{ theme: "light" | "dark" }>`
  position: fixed;
  top: 62px;
  left: 0;
  right: 0;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border-bottom: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  padding: 15px 5%;
  z-index: 99;
  box-shadow: ${({ theme }) =>
    theme === "light"
      ? "0 8px 0 " + lightTheme.accent
      : "0 8px 0 " + darkTheme.accent};

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const MobileNavLink = styled(motion.a)<{ theme: "light" | "dark" }>`
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.text};
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
  padding: 12px 16px;
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.surface : darkTheme.surface};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowSmall : darkTheme.shadowSmall};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.98);
    box-shadow: ${({ theme }) =>
      theme === "light"
        ? "2px 2px 0 " + lightTheme.border
        : "2px 2px 0 " + darkTheme.border};
  }
`;

const RightSection = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;

const Navigation: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      // Determine active section
      const sections = [
        "hero",
        "about",
        "experience",
        "projects",
        "hobbies",
        "contact",
      ];
      const scrollPosition = window.scrollY + 200;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScroll = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navItems = [
    { id: "about", label: "About" },
    { id: "experience", label: "Work" },
    { id: "projects", label: "Projects" },
    { id: "hobbies", label: "Hobbies" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <>
      <Nav theme={theme} scrolled={scrolled}>
        <Logo
          theme={theme}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          onClick={(e) => handleScroll(e, "hero")}
        >
          <LogoImage src="/icons/me.webp" alt="Trinh Van Thuan" />
        </Logo>

        <DesktopNavLinks>
          {navItems.map((item, index) => (
            <NavLink
              key={item.id}
              theme={theme}
              active={activeSection === item.id}
              onClick={(e) => handleScroll(e, item.id)}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {item.label}
            </NavLink>
          ))}
        </DesktopNavLinks>

        <RightSection>
          <ThemeToggle
            theme={theme}
            onClick={toggleTheme}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {theme === "light" ? <HiMoon /> : <HiSun />}
          </ThemeToggle>

          <MobileMenuButton
            theme={theme}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
          >
            {mobileMenuOpen ? <HiX /> : <HiMenu />}
          </MobileMenuButton>
        </RightSection>
      </Nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileNav
            theme={theme}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavLinks>
              {navItems.map((item) => (
                <MobileNavLink
                  key={item.id}
                  theme={theme}
                  onClick={(e) => handleScroll(e, item.id)}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.label}
                </MobileNavLink>
              ))}
            </MobileNavLinks>
          </MobileNav>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
